/* hs-wishlist-track.js — emits AddToWishlist to Meta and add_to_wishlist to GA4.
 *
 * Deliberately a SEPARATE file from wishlist.js. wishlist.js owns the shopper's data and
 * must keep working if analytics is disabled, blocked, or absent; this file only listens.
 * It hooks the 'hs:wishlist:change' event wishlist.js already dispatches, so the data layer
 * needed no change at all.
 *
 * ⚠ UNITS. The button carries data-price and the wishlist stores `p` in PAISE (Shopify's
 * minor unit). Meta and GA4 both expect the MAJOR unit — rupees. Everything sent from here
 * is divided by 100 exactly once, at the boundary, and never again anywhere else. This store has
 * already been burned by a 100x paise defect that inflated reported conversion value ~9900%,
 * so the conversion is done in one named helper and asserted before send.
 *
 * Fires on ADD only. Removals are not a Meta standard event and firing one would pollute
 * the audience with negative intent.
 */
(function () {
  'use strict';

  // Same master switch the first-party pixel uses, so there is one control, not two.
  if (window.HS_TRACKING_ENABLED !== true) return;

  var CURRENCY = 'INR';
  var SEEN_KEY = 'hs_wl_tracked_v1';

  /* One conversion point, paise -> rupees. Returns null rather than a wrong number:
     a missing value is recoverable, a wrong value trains bidding on a lie. */
  function paiseToMajor(paise) {
    var n = Number(paise);
    if (!isFinite(n) || n <= 0) return null;
    return Math.round(n) / 100;
  }

  function uuid() {
    if (window.crypto && crypto.randomUUID) { try { return crypto.randomUUID(); } catch (e) {} }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  /* Toggling a heart off and on again should not look like fresh intent every time.
     Once per product per session keeps the signal honest without losing a genuine
     re-save days later (sessionStorage dies with the tab). */
  function alreadyTracked(handle) {
    try {
      var seen = JSON.parse(sessionStorage.getItem(SEEN_KEY) || '[]');
      if (seen.indexOf(handle) !== -1) return true;
      seen.push(handle);
      sessionStorage.setItem(SEEN_KEY, JSON.stringify(seen.slice(-200)));
      return false;
    } catch (e) {
      return false; // private mode: fire rather than go silent
    }
  }

  /* Read what wishlist.js just stored, rather than trusting the click target —
     the item is written before the event is dispatched, and this also picks up the
     live variant the PDP resolved. */
  function storedItem(handle) {
    try {
      var d = JSON.parse(localStorage.getItem('hs_wishlist_v1') || '{}');
      var items = (d && d.items) || [];
      for (var i = 0; i < items.length; i++) if (items[i].h === handle) return items[i];
    } catch (e) {}
    return null;
  }

  /* The card's aria-label is authored as "Add <title> to wishlist", which is the only
     place the product title is available client-side. Best-effort; never blocks the send. */
  function titleFor(handle) {
    try {
      var btn = document.querySelector('[data-hs-wishlist-toggle][data-handle="' + handle + '"]');
      var raw = btn && (btn.getAttribute('data-label-out') || btn.getAttribute('aria-label'));
      if (!raw) return null;
      return raw.replace(/^Add\s+/i, '').replace(/\s+to wishlist$/i, '').trim() || null;
    } catch (e) { return null; }
  }

  function onChange(e) {
    var detail = (e && e.detail) || {};
    if (detail.op !== 'add') return;
    var handle = detail.handle;
    if (!handle || alreadyTracked(handle)) return;

    var item = storedItem(handle) || {};
    // Prefer the variant id: it is what Meta/Google catalogues key on for this store.
    var contentId = item.v ? String(item.v) : handle;
    var value = paiseToMajor(item.p);
    var title = titleFor(handle);

    /* Shared id so the browser event and any future server-side (CAPI) copy of the
       same action collapse into ONE event instead of double-counting. */
    var eventId = 'wl_' + handle + '_' + Date.now() + '_' + uuid().slice(0, 8);

    // ── Meta ────────────────────────────────────────────────────────────────
    if (typeof window.fbq === 'function') {
      var payload = {
        content_type: 'product',
        content_ids: [contentId],
        content_category: 'wishlist',
      };
      if (title) payload.content_name = title;
      if (value !== null) {
        payload.value = value;              // rupees, converted once above
        payload.currency = CURRENCY;
        payload.contents = [{ id: contentId, quantity: 1, item_price: value }];
      }
      try { window.fbq('track', 'AddToWishlist', payload, { eventID: eventId }); } catch (err) {}
    }

    // ── GA4 (recommended event name is snake_case) ──────────────────────────
    if (typeof window.gtag === 'function') {
      var ga = { items: [{ item_id: contentId, item_name: title || handle, quantity: 1 }] };
      if (value !== null) {
        ga.currency = CURRENCY;
        ga.value = value;
        ga.items[0].price = value;
      }
      try { window.gtag('event', 'add_to_wishlist', ga); } catch (err) {}
    }

    /* Let anything else on the page (server-side relay, future CAPI bridge) reuse the
       SAME event id — that is what makes deduplication possible later. */
    try {
      document.dispatchEvent(new CustomEvent('hs:wishlist:tracked', {
        detail: { handle: handle, variantId: item.v || null, valueMajor: value, currency: CURRENCY, eventId: eventId },
      }));
    } catch (err) {}
  }

  document.addEventListener('hs:wishlist:change', onChange);
})();
